import ConstructorIOBrowser from "@constructor-io/constructorio-client-javascript";
import type ConstructorIONode from "@constructor-io/constructorio-node";
import { CONSTRUCTOR_SECTIONS } from "src/constants/constructor";
import { constructorIndexKey } from "src/config/app-config";
import { logger } from "src/utils/helper/logger";
import { isNativeApp } from "src/utils/isNativeApp";

export function getConstructorIndex() {
  return constructorIndexKey;
}

export const INTERNAL_SERVICE_SEGMENT = "internal-service";

// Single API surface for both envs. Methods are typed against the Browser SDK
// because that's the response shape adapter code already consumes. The server
// wrapper translates Node SDK responses into this shape internally.
type ConstructorClientCommon = {
  setUserId(userId?: string): void;
  browse: ConstructorIOBrowser["browse"]["getBrowseResults"];
  autocomplete: ConstructorIOBrowser["autocomplete"]["getAutocompleteResults"];
  search: ConstructorIOBrowser["search"]["getSearchResults"];
  recommendations: ConstructorIOBrowser["recommendations"]["getRecommendations"];
};

export type BrowserConstructorClient = ConstructorClientCommon & {
  env: "browser";
  tracker: ConstructorIOBrowser["tracker"];
};

export type ServerConstructorClient = ConstructorClientCommon & {
  env: "server";
};

export type ConstructorClient =
  | BrowserConstructorClient
  | ServerConstructorClient;

type BrowseResponse = Awaited<ReturnType<ConstructorClientCommon["browse"]>>;
type AutocompleteResponse = Awaited<
  ReturnType<ConstructorClientCommon["autocomplete"]>
>;
type SearchResponse = Awaited<ReturnType<ConstructorClientCommon["search"]>>;
type RecommendationsResponse = Awaited<
  ReturnType<ConstructorClientCommon["recommendations"]>
>;

let browserSingleton: ConstructorIOBrowser | null = null;

function getBrowserSingleton(): ConstructorIOBrowser {
  if (!browserSingleton) {
    browserSingleton = new ConstructorIOBrowser({
      apiKey: getConstructorIndex(),
      // Must match the beacon, otherwise the SDK moves the client id out of localStorage.
      idOptions: { client_id_storage_location: "local" },
    });
  }

  return browserSingleton;
}

/**
 * Makes sure the visitor has a Constructor client id and session before anything reads them.
 * Creating the browser client writes both to the localStorage keys the beacon reads, so the
 * beacon reuses them, and a carousel request sent before the beacon has loaded still carries
 * the visitor's own ids. Constructor asked for this instead of ids made up on the backend.
 * Skipped in the apps' web views: the beacon is off there and the apps send their own ids.
 */
export function ensureConstructorIdentity(): void {
  if (typeof window === "undefined" || isNativeApp || !getConstructorIndex()) {
    return;
  }

  try {
    getBrowserSingleton();
  } catch (error) {
    logger({
      message: "Error creating the Constructor identity",
      error: error as Error,
    });
  }
}

function createBrowserConstructorClient(): BrowserConstructorClient {
  const client = getBrowserSingleton();

  return {
    env: "browser",
    setUserId(userId) {
      if (userId) {
        client.setClientOptions({
          apiKey: getConstructorIndex(),
          userId,
        });
      }
    },
    browse: client.browse.getBrowseResults.bind(client.browse),
    autocomplete: client.autocomplete.getAutocompleteResults.bind(
      client.autocomplete
    ),
    search: client.search.getSearchResults.bind(client.search),
    recommendations: client.recommendations.getRecommendations.bind(
      client.recommendations
    ),
    tracker: client.tracker,
  };
}

let serverSingleton: ConstructorIONode | null = null;

function getServerSingleton(): ConstructorIONode {
  if (!serverSingleton) {
    // Lazy require so the Node SDK is never resolved in the browser bundle
    // (also stubbed out via the `browser` field in package.json).
    const ConstructorIONodeCtor =
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      require("@constructor-io/constructorio-node") as typeof ConstructorIONode;
    serverSingleton = new ConstructorIONodeCtor({
      apiKey: getConstructorIndex(),
    });
  }

  return serverSingleton;
}

function createServerConstructorClient(): ServerConstructorClient {
  const client = getServerSingleton();
  let userParameters: { userId?: string; segments?: string[] } = {
    segments: [INTERNAL_SERVICE_SEGMENT],
  };

  const browse: ConstructorClientCommon["browse"] = async (
    facetName,
    facetValue,
    parameters,
    networkParameters
  ) => {
    // Browser & Node SDKs use different `BrowseParameters` types (e.g. nested
    // `variationsMap.group_by` is optional in the Browser SDK and required in
    // the Node SDK). Pass through as an untyped record at the SDK boundary.
    const nodeParameters = (parameters || {}) as Record<string, unknown>;

    if (facetName === "id") {
      const searchResponse = await client.browse.getBrowseResultsForItemIds(
        facetValue.split(","),
        nodeParameters,
        userParameters,
        networkParameters
      );

      return {
        request: searchResponse.request,
        result_id: searchResponse.result_id,
        response: searchResponse.response,
      } as BrowseResponse;
    }

    const searchResponse = await client.browse.getBrowseResults(
      facetName,
      facetValue,
      {
        ...nodeParameters,
        filters: {
          ...((nodeParameters.filters as Record<string, unknown>) || {}),
        },
      },
      userParameters,
      networkParameters
    );

    return {
      request: searchResponse.request,
      result_id: searchResponse.result_id,
      response: searchResponse.response,
    } as BrowseResponse;
  };

  const autocomplete: ConstructorClientCommon["autocomplete"] = async (
    query,
    parameters,
    networkParameters
  ) => {
    const resultsPerPage =
      parameters?.resultsPerSection?.[CONSTRUCTOR_SECTIONS.PRODUCTS] ||
      parameters?.numResults;

    const searchResponse = await client.search.getSearchResults(
      query,
      {
        section: CONSTRUCTOR_SECTIONS.PRODUCTS,
        resultsPerPage,
        fmtOptions: parameters?.fmtOptions,
        filters: parameters?.filters,
      },
      userParameters,
      networkParameters
    );

    const data =
      (searchResponse.response as
        | { results?: unknown[]; total_num_results?: number }
        | undefined) || {};

    return {
      request: searchResponse.request || {},
      result_id: searchResponse.result_id || "",
      sections: {
        [CONSTRUCTOR_SECTIONS.PRODUCTS]: data.results || [],
      },
      total_num_results_per_section: {
        [CONSTRUCTOR_SECTIONS.PRODUCTS]: data.total_num_results ?? 0,
      },
    } as AutocompleteResponse;
  };

  const search: ConstructorClientCommon["search"] = async (
    query,
    parameters,
    networkParameters
  ) => {
    const { response, result_id, request } =
      await client.search.getSearchResults(
        query,
        {
          filters: parameters?.filters,
          fmtOptions: parameters?.fmtOptions,
          hiddenFacets: parameters?.hiddenFacets,
          hiddenFields: parameters?.hiddenFields,
          offset: parameters?.offset,
          page: parameters?.page,
          preFilterExpression: parameters?.preFilterExpression,
          qsParam: parameters?.qsParam,
          resultsPerPage: parameters?.resultsPerPage,
          section: parameters?.section,
          sortBy: parameters?.sortBy,
          sortOrder: parameters?.sortOrder,
        },
        userParameters,
        networkParameters
      );

    return {
      request: request || {},
      result_id: result_id || "",
      response,
    } as SearchResponse;
  };

  const recommendations: ConstructorClientCommon["recommendations"] = async (
    podId,
    parameters,
    networkParameters
  ) => {
    const response = await client.recommendations.getRecommendations(
      podId,
      {
        filters: parameters?.filters,
        hiddenFields: parameters?.hiddenFields,
        itemIds: parameters?.itemIds,
        numResults: parameters?.numResults,
        preFilterExpression: parameters?.preFilterExpression,
        section: parameters?.section,
        term: parameters?.term,
        variationId: parameters?.variationId,
      },
      userParameters,
      networkParameters
    );

    return response as RecommendationsResponse;
  };

  return {
    env: "server",
    setUserId(userId) {
      userParameters = userId
        ? { userId }
        : { segments: [INTERNAL_SERVICE_SEGMENT] };
    },
    browse,
    autocomplete,
    search,
    recommendations,
  };
}

export function getConstructorClient(env: "server"): ServerConstructorClient;
export function getConstructorClient(env: "browser"): BrowserConstructorClient;
export function getConstructorClient(
  env?: "server" | "browser"
): ConstructorClient;
export function getConstructorClient(
  env?: "server" | "browser"
): ConstructorClient {
  const resolved =
    env || (typeof window === "undefined" ? "server" : "browser");

  return resolved === "server"
    ? createServerConstructorClient()
    : createBrowserConstructorClient();
}
